export function serverQueryParams(q, offset, limit, count) {
    const p = new URLSearchParams({ offset: String(offset), limit: String(limit) });
    if (q.sort.length)
        p.set('sort', q.sort.map(k => `${k.col}:${k.dir}`).join(','));
    if (q.filter.length)
        p.set('filter', JSON.stringify(q.filter));
    if (q.quick)
        p.set('quick', q.quick);
    if (count)
        p.set('count', 'true');
    return p.toString();
}
/** 그룹 키 → 조건 (빈 값은 empty) */
export function keyPred(col, key) { return key == null || key === '' ? { col, op: 'empty' } : { col, op: '=', value: key }; }
/** 서버 그룹 행 → 노드 */
export function groupNodes(rows, col, level, parent) {
    return rows.map(r => {
        const key = r.key == null ? null : /^\d{4}-\d\d-\d\d/.test(String(r.key)) ? String(r.key).slice(0, 10) : String(r.key);
        return { key, level, col, path: (parent ? parent.path + '\u0001' : '') + (key ?? '∅'), count: Number(r.count), aggs: r.aggs ?? {}, preds: [...(parent?.preds ?? []), keyPred(col, key)], children: null, rows: new Uint32Array(0), loaded: false, loading: false };
    });
}
/**
 * 서버 그룹 표시 목록 (순수): 펼친 노드를 따라 disp(음수 = 그룹 머리행)·그룹 목록. 펼쳤는데 아직 안 받은 노드는 want(node)
 */
export function flattenServerGroups(roots, isCollapsed, labelOf, want) {
    const disp = [], groups = [];
    const walk = (ns) => {
        for (const n of ns) {
            const collapsed = isCollapsed(n.path);
            groups.push({ kind: 'group', key: n.key, label: labelOf(n.col, n.key), level: n.level, rows: n.rows, collapsed, col: n.col, path: n.path, count: n.count, node: n });
            disp.push(-groups.length);
            if (collapsed)
                continue;
            if (n.children)
                walk(n.children);
            else if (n.loaded)
                for (let i = 0; i < n.rows.length; i++)
                    disp.push(n.rows[i]);
            else
                want(n);
        }
    };
    walk(roots);
    return { disp: Int32Array.from(disp), groups };
}
export class ServerRowModel {
    constructor(ds, src, onChange) {
        this.ds = ds;
        this.src = src;
        this.onChange = onChange;
        this.total = 0;
        this.loaded = new Set();
        this.pending = new Set();
        this.gen = 0;
        this.q = { sort: [], filter: [], quick: '' };
        this.sig = '';
        this.timer = null;
        this.want = [0, 0];
        this.loading = false;
        this.error = null;
        this.blockSize = Math.max(20, src.blockSize ?? 200);
    }
    /** 1.2.0: url 에 ?a=b 가 있어도 /groups·/distinct 는 경로 뒤에 붙임 (예전에는 질의 문자열 뒤에 붙어 404) */
    url(qs, sub = '') { const i = this.src.url.indexOf('?'), path = i < 0 ? this.src.url : this.src.url.slice(0, i), q0 = i < 0 ? '' : this.src.url.slice(i + 1); return path + sub + '?' + (q0 ? q0 + '&' : '') + qs; }
    async get(url) {
        const res = await fetch(url, { headers: { Accept: 'application/json', ...(this.src.headers ?? {}) } });
        if (!res.ok)
            throw new Error(`서버 조회 실패 (${res.status})`);
        return res.json();
    }
    /** 조건이 바뀌었으면 처음부터 다시 (건수 + 첫 블록) */
    setQuery(q, force = false) {
        const sig = JSON.stringify(q);
        if (!force && sig === this.sig)
            return false;
        this.sig = sig;
        this.q = q;
        const gen = ++this.gen;
        this.loaded.clear();
        this.pending.clear();
        this.loading = true;
        this.error = null;
        this.get(this.url(serverQueryParams(q, 0, this.blockSize, true))).then((d) => {
            if (gen !== this.gen)
                return;
            this.total = Math.max(0, Number(d.total ?? d.n) || 0); // total 이 없으면 n (예전에는 NaN)
            this.loading = false;
            this.ds.allocate(this.total); // 'load' 이벤트 → 그리드 다시 그림
            this.ds.fillColumnar(0, d);
            this.loaded.add(0);
            this.onChange('reset');
            this.ensure(this.want[0], this.want[1]);
        }).catch(e => { if (gen === this.gen) {
            this.loading = false;
            this.error = String(e.message ?? e);
            this.onChange('reset');
        } });
        return true;
    }
    /** 오류 뒤 다시 시도 (같은 조건으로 처음부터) */
    retry() { return this.setQuery(this.q, true); }
    isLoaded(row) { return this.loaded.has(Math.floor(row / this.blockSize)); }
    get loadedBlocks() { return this.loaded.size; }
    /** 보이는 구간 [r0, r1) 의 블록 요청 (스크롤 중 연타 방지로 잠깐 모아서) */
    ensure(r0, r1) {
        this.want = [r0, r1];
        if (this.loading || this.timer)
            return;
        this.timer = setTimeout(() => {
            this.timer = null;
            const [a, b] = this.want, bs = this.blockSize, gen = this.gen;
            for (let blk = Math.floor(a / bs); blk * bs < Math.min(b, this.total); blk++) {
                if (this.loaded.has(blk) || this.pending.has(blk))
                    continue;
                this.pending.add(blk);
                this.get(this.url(serverQueryParams(this.q, blk * bs, bs, false))).then((d) => {
                    if (gen !== this.gen)
                        return;
                    this.pending.delete(blk);
                    this.loaded.add(blk);
                    this.ds.fillColumnar(blk * bs, d);
                    this.onChange('block');
                }).catch(() => { if (gen === this.gen)
                    this.pending.delete(blk); });
            }
        }, 40);
    }
    /** 진행 중인 블록 요청을 버리고 멈춤 (서버 그룹 모드로 바뀔 때). 다시 setQuery(…, true) 로 시작 */
    suspend() { this.gen++; this.sig = ''; this.loaded.clear(); this.pending.clear(); this.loading = false; if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
    } }
    /** 그룹(GROUP BY col) 또는 전체 합계(col null) — extra 는 상위 그룹 키 조건 */
    async groups(col, q, extra, aggs, dir) {
        const p = new URLSearchParams();
        if (col)
            p.set('group', col);
        const f = [...q.filter, ...extra];
        if (f.length)
            p.set('filter', JSON.stringify(f));
        if (q.quick)
            p.set('quick', q.quick);
        if (aggs.length)
            p.set('agg', aggs.join(','));
        if (dir === 'desc')
            p.set('dir', 'desc');
        return await this.get(this.url(p.toString(), '/groups'));
    }
    /** 그룹 멤버를 받아 Dataset 끝에 붙임 (이벤트 없음) → 물리 행 번호들. limit 건까지, 블록 크기씩 */
    async members(q, extra, count, limit = 10000) {
        const want = Math.min(count, limit), out = [], step = Math.max(this.blockSize, 2000);
        for (let off = 0; off < want; off += step) {
            const d = await this.get(this.url(serverQueryParams({ ...q, filter: [...q.filter, ...extra] }, off, Math.min(step, want - off), false)));
            const base = this.ds.appendColumnarSilent(d);
            for (let i = 0; i < d.n; i++)
                out.push(base + i);
            if (d.n < Math.min(step, want - off))
                break;
        }
        return Uint32Array.from(out);
    }
    /** 헤더 필터 메뉴 값 목록 (그 컬럼 조건은 뺀 필터로) */
    async distinct(col, filter, quick) {
        const p = new URLSearchParams({ col });
        if (filter.length)
            p.set('filter', JSON.stringify(filter));
        if (quick)
            p.set('quick', quick);
        const rows = await this.get(this.url(p.toString(), '/distinct'));
        return rows.map(r => ({ key: r.key == null ? '' : String(r.key).slice(0, 10).length === 10 && /^\d{4}-\d\d-\d\d/.test(String(r.key)) ? String(r.key).slice(0, 10) : String(r.key), count: Number(r.count) }));
    }
}
